import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../redux/initialStoreState";
import { useDispatchWrapper } from "./useDispatch";
import { LoadState } from "../constants/enums";
import { initialInsurerState } from "../redux/insurer/insurerState";
import { fetchInsurerCodeWithCallbackAsync } from "../redux/insurer/insurerActions";
import { initialBranchOfficeState } from "../redux/branchOffice/branchOfficeState";
import { fetchBranchOfficeWithCallback } from "../redux/branchOffice/branchOfficeActions";
import {
  clearCustomerPolicyDetailsState,
  fetchCustomerPolicyDetailsAsync,
} from "../redux/customerPolicyDetails/customerPolicyDetailsActions";

/**
 * Generic hook returning the current merged customer policy details for any
 * policy type. Replaces the per-policy customer-info and policy-admin fetch
 * hooks: the merged GET returns admin data, and this hook preserves the
 * insurer/branch enrichment that downstream consumers (certificates,
 * endorsements, authorization forms) depend on.
 *
 * When `customerPolicyId` is undefined, no fetch is dispatched and the current
 * (unloaded) state is returned. Preserves the insurer/branch enrichment that
 * the former per-policy policy-admin hooks provided.
 */
export const useCustomerPolicyDetails = (
  customerPolicyId?: number,
  // When true, the hook only READS the already-loaded shared slice and does
  // NOT dispatch a fetch (or clear) on mount. Used by panels that open on top
  // of a screen which already loaded the details (e.g. the Vehicle "Add" panel
  // inside the policy-change flow), so mounting them doesn't flip the shared
  // loading slice — which would blank the parent Outlet and remount the form.
  skipFetch?: boolean
) => {
  const details = useSelector(
    (storeState: IStoreState) => storeState.customerPolicyDetails.details
  );
  const [insurer, setInsurer] = React.useState(
    initialInsurerState.insurerCode.data
  );
  const [branch, setBranch] = React.useState(
    initialBranchOfficeState.branchOffice.data
  );
  const dispatch = useDispatchWrapper();

  React.useEffect(() => {
    if (skipFetch) {
      return;
    }
    if (customerPolicyId) {
      dispatch(fetchCustomerPolicyDetailsAsync(customerPolicyId));
    } else {
      // New-policy mode (no customerPolicyId): reset the shared slice so the
      // form starts from the initial empty record instead of showing the
      // previously opened policy's details.
      dispatch(clearCustomerPolicyDetailsState());
    }
  }, [customerPolicyId, skipFetch]);

  React.useEffect(() => {
    if (
      details.data &&
      details.loading === LoadState.Loaded &&
      details.data.insurer
    ) {
      dispatch(
        fetchInsurerCodeWithCallbackAsync(details.data.insurer, (data) => {
          if (data) {
            setInsurer(data);
          }
        })
      );
    }
  }, [details]);

  React.useEffect(() => {
    if (
      details.data &&
      details.loading === LoadState.Loaded &&
      details.data.branch_code
    ) {
      dispatch(
        fetchBranchOfficeWithCallback(details.data.branch_code, (data) => {
          if (data) {
            setBranch(data);
          }
        })
      );
    }
  }, [details]);

  return { details, loading: details.loading, insurer, branch };
};
